// [PM-PERF-REPORT 2026-07-13] Download a per-PM PDF from the Overall PM Performance data,
// matching the reference "PM Performance Jan Jun 26.pdf". One blue-header table per PM,
// one row per SELECTED month the PM actually has inventory in (months with no inventory are
// skipped — e.g. a PM who joined in March starts at March), plus a bold Total row.
//
// Two-step selection: the dialog first fetches the selected months' data (fetchPmReportData)
// to list the PMs present (pmNamesInReport), the user ticks the PMs they want, then the PDF
// is built from that SAME data limited to the chosen PMs (downloadPmPerformanceReport) — no
// second fetch.
//
// Column mapping (per PM, per month), from the Overall PM Performance rows:
//   Month             = the selected month (MMM-YY)
//   Inventory         = effectiveInventory   (Placed Inventory)
//   Retention In Bank = retainedRetention    (the "Retained" column)
//   Retention%        = Retention In Bank / Inventory
//   Recurring SO      = recurringUpsell       (Recurring Upsell)
//   One Time SO       = oneTimeUpsell         (One-Time Upsell)
//   Total SO          = totalUpsell           (Total Upsell)
//   Refund/Dispute    = refundsDisputes       [PM-PERF-RD 2026-08-25] same two columns as the table
//   Dispute Won       = disputesWon
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchTeamPerformance, TeamPerformanceRow } from '../services/api';

// Single source of truth for the report's columns — used for the PDF header AND the
// "Report columns" preview in the download dialog, so the two can never drift.
export const PM_REPORT_COLUMNS = [
  'Month', 'Inventory', 'Retention In Bank', 'Retention%', 'Recurring SO', 'One Time SO', 'Total SO',
  'Refund/Dispute', 'Dispute Won', // [PM-PERF-RD 2026-08-25]
] as const;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MON_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const money = (n: number) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
/* [UPSELL-HEALTH 2026-08-24] Upsell is the only HubSpot-sourced figure in this report. When that
   month's fetch failed the server sends 0, which in a PDF someone files away is a wrong number
   with no way to tell. The on-screen table already renders an em-dash for it; this keeps the
   downloaded report saying the same thing rather than the two artefacts disagreeing. */
const upMoney = (n: number, ok: boolean) => (ok ? money(n) : '—');
const pct = (n: number) => (Number(n) || 0).toFixed(2) + '%';
const monthLabel = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${MON[m - 1]}-${String(y).slice(2)}`; };

function fileName(months: string[]): string {
  const s = [...months].sort();
  const [fy, fm] = s[0].split('-').map(Number);
  const [ly, lm] = s[s.length - 1].split('-').map(Number);
  return s[0] === s[s.length - 1]
    ? `PM Performance ${MON[fm - 1]} ${String(fy).slice(2)}.pdf`
    : `PM Performance ${MON[fm - 1]} ${MON[lm - 1]} ${String(ly).slice(2)}.pdf`;
}

/** [PDF-TITLE 2026-09-30, user] The months in the title, full names like the EOM PDF: "September 2026",
 *  or the span of the chosen months: "June – August 2026", "December 2025 – February 2026". */
function periodTitle(months: string[]): string {
  const s = [...months].sort();
  const [fy, fm] = s[0].split('-').map(Number);
  const [ly, lm] = s[s.length - 1].split('-').map(Number);
  if (s[0] === s[s.length - 1]) return `${MON_FULL[fm - 1]} ${fy}`;
  return fy === ly
    ? `${MON_FULL[fm - 1]} – ${MON_FULL[lm - 1]} ${ly}`
    : `${MON_FULL[fm - 1]} ${fy} – ${MON_FULL[lm - 1]} ${ly}`;
}

export interface PmReportMonth {
  month: string;
  byPm: Map<string, TeamPerformanceRow>;
  /** false = HubSpot upsell could not be read for THIS month, so its three upsell figures are
   *  0 by absence. Per-month, because the report spans several and they fail independently. */
  upsellOk: boolean;
}

/** Fetch the Overall PM Performance for each selected month (parallel, chronological). */
export async function fetchPmReportData(months: string[]): Promise<PmReportMonth[]> {
  const ordered = [...months].sort();
  return Promise.all(ordered.map(async (m) => {
    const data = await fetchTeamPerformance('overall', m);
    const byPm = new Map<string, TeamPerformanceRow>();
    for (const r of data.rows) if (r.pm) byPm.set(r.pm, r);
    // `!== false` so a payload from a server without the flag still reads as healthy.
    return { month: m, byPm, upsellOk: data.upsellOk !== false };
  }));
}

/** PMs that would appear in the report — those with inventory in >=1 selected month — sorted A→Z. */
export function pmNamesInReport(data: PmReportMonth[]): string[] {
  const names = new Set<string>();
  for (const { byPm } of data) for (const [pm, r] of byPm) if ((Number(r.effectiveInventory) || 0) > 0) names.add(pm);
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Build + download the PDF from already-fetched data, limited to `selectedPms` (all if omitted). */
export function downloadPmPerformanceReport(data: PmReportMonth[], months: string[], selectedPms?: string[]): void {
  if (!data.length) return;
  const ordered = [...months].sort();
  const pmSet = selectedPms ? new Set(selectedPms) : null;
  const pmNames = pmNamesInReport(data).filter((pm) => !pmSet || pmSet.has(pm));
  if (!pmNames.length) return;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  let y = 42;

  // [PDF-TITLE 2026-09-30, user] "PM Performance" on top of page 1, so the file can be sent to someone
  // as it is. Same size and spacing as the EOM PDF's title.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(20, 20, 20);
  doc.text(`PM Performance · ${periodTitle(ordered)}`, pageW / 2, y, { align: 'center' });
  y += 30;
  // Where a page's first table starts: under the title on page 1, at the top margin after that.
  // The first table on a page never moves on, so the title is never left alone on page 1.
  let pageTop = y;

  for (const pm of pmNames) {
    // Only the selected months where this PM actually has inventory.
    const rows: TeamPerformanceRow[] = [];
    const labels: string[] = [];
    const upOk: boolean[] = [];
    for (const { month, byPm, upsellOk } of data) {
      const r = byPm.get(pm);
      if (r && (Number(r.effectiveInventory) || 0) > 0) {
        rows.push(r); labels.push(monthLabel(month)); upOk.push(upsellOk);
      }
    }
    if (!rows.length) continue;
    /* One bad month makes the TOTAL incomplete, so the total dashes too — a total that silently
       omits a month's upsell is exactly the kind of quietly-wrong figure this whole change is
       about. `every` rather than `some`: the total is only trustworthy if all of it is. */
    const allUpOk = upOk.every(Boolean);

    const body = rows.map((r, i) => [
      labels[i], money(r.effectiveInventory), money(r.retainedRetention),
      r.effectiveInventory > 0 ? pct((r.retainedRetention / r.effectiveInventory) * 100) : '0.00%',
      upMoney(r.recurringUpsell, upOk[i]), upMoney(r.oneTimeUpsell, upOk[i]), upMoney(r.totalUpsell, upOk[i]),
      money(r.refundsDisputes), money(r.disputesWon), // [PM-PERF-RD 2026-08-25] DB-sourced, never dashed
    ]);
    const sum = (f: (r: TeamPerformanceRow) => number) => rows.reduce((s, r) => s + (Number(f(r)) || 0), 0);
    const tInv = sum((r) => r.effectiveInventory), tRet = sum((r) => r.retainedRetention);
    /* [PDF-TOTAL-ALIGN 2026-09-29, user] jspdf-autotable applies `columnStyles` to BODY rows only
       (its cellStyles: `sectionName === 'body' ? columnStyles : {}`), so the Total row fell back to
       left-aligned figures under right-aligned columns. The foot now right-aligns (footStyles below)
       and only the "Total" label keeps the Month column's left alignment. */
    const total = [{ content: 'Total', styles: { halign: 'left' as const } }, money(tInv), money(tRet), tInv > 0 ? pct((tRet / tInv) * 100) : '0.00%',
      upMoney(sum((r) => r.recurringUpsell), allUpOk), upMoney(sum((r) => r.oneTimeUpsell), allUpOk),
      upMoney(sum((r) => r.totalUpsell), allUpOk),
      money(sum((r) => r.refundsDisputes)), money(sum((r) => r.disputesWon))]; // [PM-PERF-RD 2026-08-25]

    /* [PDF-KEEP-TOGETHER 2026-09-29, user] Keep each PM's table on ONE page. The old check only
       asked for 120pt of room, so a 3-month table starting low on a page was split: the first
       part ended with a Total row that already included the months printed on the next page
       (autotable repeats the foot on every page), and the rest carried no PM name. Height =
       title gap 10 + two-line header ~29 + ~18.4 per row (months + Total) + the upsell note. */
    const tableH = 10 + 29 + 18.4 * (rows.length + 1) + (allUpOk ? 0 : 12) + 4;
    if (y > pageH - 120 || (y > pageTop && y + tableH > pageH - 40)) { doc.addPage(); y = 42; pageTop = 42; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(20, 20, 20);
    doc.text(pm, pageW / 2, y, { align: 'center' });

    autoTable(doc, {
      startY: y + 10,
      // [PDF-HEAD-ALIGN 2026-09-30, user] "the alignment should be proper for all the data": a number
      // column's heading now sits on the same right edge as its figures and its Total (it was centred).
      // Month stays left, like the month labels under it. autotable ignores columnStyles for the head.
      head: [PM_REPORT_COLUMNS.map((c, i) => ({ content: c, styles: { halign: (i === 0 ? 'left' : 'right') as 'left' | 'right' } }))],
      body,
      foot: [total],
      // [PDF-KEEP-TOGETHER 2026-09-29] Only if a table is ever taller than a whole page (a very
      // long month range): print its Total once, at the end, never under a partial list.
      showFoot: 'lastPage',
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4, lineColor: [200, 200, 200], lineWidth: 0.5, textColor: [0, 0, 0] },
      headStyles: { fillColor: [68, 114, 196], textColor: 255, fontStyle: 'bold', halign: 'center' },
      footStyles: { fillColor: [222, 230, 241], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'right' }, // [PDF-TOTAL-ALIGN 2026-09-29]
      columnStyles: {
        0: { halign: 'left' }, 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' },
        4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' },
        7: { halign: 'right' }, 8: { halign: 'right' }, // [PM-PERF-RD 2026-08-25]
      },
      bodyStyles: { halign: 'right', textColor: [0, 0, 0] },
      margin: { left: 40, right: 40 },
    });
    y = (doc as any).lastAutoTable.finalY;
    /* An em-dash in a PDF with no explanation is worse than useless to whoever opens it later,
       so name the months it applies to. Only drawn when something actually failed. */
    if (!allUpOk) {
      const bad = labels.filter((_, i) => !upOk[i]).join(', ');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(150, 60, 20);
      doc.text(`Upsell could not be read from HubSpot for ${bad} — shown as "—" rather than $0.`, 40, y + 12);
      doc.setTextColor(0, 0, 0);
      y += 12;
    }
    y += 26;
  }

  doc.save(fileName(ordered));
}
